import React from "react";
import {
    ArrowRight,
    Check,
    Megaphone,
    PencilLine,
    Search,
    Send,
    Sparkles,
    UserRound,
    Wand2,
    Workflow,
} from "lucide-react";

// Renders a capability-specific system diagram in the right column of the
// capabilities deep-dive section. Each variant is a self-contained editorial
// visualization built for a non-specialist audience: plain-English labels,
// friendly role names, and visual metaphors that explain the capability at a
// glance without designer / marketing-ops jargon.
export const CapabilityDiagram = ({ id }) => {
    if (id === "creative-systems") return <CreativeSystemsDiagram />;
    if (id === "ai-agent-workflows") return <AgentWorkflowDiagram />;
    if (id === "human-talent") return <TalentNetworkDiagram />;
    if (id === "growth-engines") return <GrowthEngineDiagram />;
    return null;
};

// ---------- 01 / Creative Systems ----------
const SWATCHES = [
    { hex: "#0E1A2E" },
    { hex: "#2563EB" },
    { hex: "#BB7621" },
    { hex: "#F7F2E8" },
    { hex: "#E8E1D1" },
];

const CreativeSystemsDiagram = () => (
    <div className="flex flex-col gap-4">
        <Row label="Brand colors">
            <div className="flex items-center gap-2">
                {SWATCHES.map((s) => (
                    <span
                        key={s.hex}
                        className="h-8 w-8 rounded-lg ring-1 ring-black/10"
                        style={{ background: s.hex }}
                    />
                ))}
            </div>
        </Row>

        <Row label="Typography">
            <div className="flex items-end justify-between gap-3">
                <div>
                    <div className="font-display text-5xl leading-none text-[hsl(var(--text))] tracking-[-0.02em]">
                        Aa
                    </div>
                    <div className="mt-1 text-[10px] font-mono uppercase tracking-[0.18em] text-[hsl(var(--text-subtle))]">
                        Headlines
                    </div>
                </div>
                <div className="text-right">
                    <div className="font-sans text-2xl font-semibold text-[hsl(var(--text))] tracking-[-0.01em]">
                        Aa
                    </div>
                    <div className="mt-1 text-[10px] font-mono uppercase tracking-[0.18em] text-[hsl(var(--text-subtle))]">
                        Body text
                    </div>
                </div>
            </div>
        </Row>

        <Row label="Logo & wordmark" className="!pb-0">
            <div className="flex items-center gap-3">
                <span className="inline-flex h-9 w-9 items-center justify-center rounded-xl bg-[rgba(37,99,235,0.10)] ring-1 ring-[rgba(37,99,235,0.30)] text-[hsl(var(--blue))]">
                    <Sparkles className="h-4 w-4" />
                </span>
                <span className="font-display text-xl text-[hsl(var(--text))] tracking-[-0.01em]">
                    YourBrand
                </span>
                <span className="ml-auto text-[10px] font-mono uppercase tracking-[0.18em] text-[hsl(var(--text-subtle))]">
                    Consistent · everywhere
                </span>
            </div>
        </Row>
    </div>
);

// ---------- 02 / AI Agent Workflows ----------
const AGENT_NODES = [
    { id: "research", label: "Research", caption: "Gathers information", Icon: Search, status: "done" },
    { id: "draft", label: "Drafting", caption: "Writes a first version", Icon: PencilLine, status: "done" },
    { id: "review", label: "Review", caption: "Checks quality & brand", Icon: Check, status: "done" },
    { id: "publish", label: "Publish", caption: "Ships the work", Icon: Send, status: "live" },
];

const AgentWorkflowDiagram = () => (
    <div className="flex flex-col gap-2.5">
        {AGENT_NODES.map((n, i) => (
            <React.Fragment key={n.id}>
                <div className="flex items-center justify-between gap-3 rounded-xl border border-black/[0.08] bg-white px-3.5 py-2.5">
                    <div className="flex items-center gap-3 min-w-0">
                        <span className="inline-flex h-8 w-8 shrink-0 items-center justify-center rounded-lg bg-[rgba(37,99,235,0.10)] ring-1 ring-[rgba(37,99,235,0.30)] text-[hsl(var(--blue))]">
                            <n.Icon className="h-3.5 w-3.5" />
                        </span>
                        <div className="min-w-0">
                            <div className="text-[13px] text-[hsl(var(--text))] leading-tight">
                                {n.label}
                            </div>
                            <div className="text-[11px] text-[hsl(var(--text-subtle))] leading-tight mt-0.5 truncate">
                                {n.caption}
                            </div>
                        </div>
                    </div>
                    <span
                        className={`text-[10px] font-mono uppercase tracking-[0.18em] px-2 py-0.5 rounded-full ${
                            n.status === "live"
                                ? "text-[hsl(var(--gold))] bg-[rgba(187,118,33,0.10)]"
                                : "text-[hsl(var(--blue))] bg-[rgba(37,99,235,0.08)]"
                        }`}
                    >
                        {n.status}
                    </span>
                </div>
                {i < AGENT_NODES.length - 1 && (
                    <div className="flex items-center gap-2 pl-4 text-[hsl(var(--text-subtle))]">
                        <span className="h-2 w-px bg-[hsl(var(--text-subtle))]/40" />
                        <ArrowRight className="h-3 w-3 -ml-1.5" />
                    </div>
                )}
            </React.Fragment>
        ))}
    </div>
);

// ---------- 03 / Human Talent Network ----------
const TALENT_ROLES = [
    { angle: -90, label: "Strategy", Icon: Sparkles },
    { angle: -30, label: "Creative", Icon: PencilLine },
    { angle: 30, label: "Design", Icon: Wand2 },
    { angle: 90, label: "Engineering", Icon: Workflow },
    { angle: 150, label: "Editorial", Icon: PencilLine },
    { angle: 210, label: "Marketing", Icon: Megaphone },
];

const TalentNetworkDiagram = () => {
    const radius = 84;
    return (
        <div className="relative h-[260px] flex items-center justify-center">
            {/* connector lines */}
            <svg
                viewBox="-120 -120 240 240"
                className="absolute inset-0 h-full w-full"
                aria-hidden="true"
            >
                {TALENT_ROLES.map((r, i) => {
                    const rad = (r.angle * Math.PI) / 180;
                    const x = Math.cos(rad) * radius;
                    const y = Math.sin(rad) * radius;
                    return (
                        <line
                            key={i}
                            x1={0}
                            y1={0}
                            x2={x}
                            y2={y}
                            stroke="rgba(20,30,50,0.18)"
                            strokeWidth="0.8"
                            strokeDasharray="3 4"
                        />
                    );
                })}
            </svg>
            {/* center hub */}
            <div className="relative z-10 inline-flex flex-col items-center justify-center h-20 w-20 rounded-full bg-[hsl(var(--text))] text-[hsl(var(--bg))] shadow-deep px-2 text-center">
                <span className="text-[9.5px] font-mono uppercase tracking-[0.18em] opacity-75">
                    Your
                </span>
                <span className="font-display text-[15px] leading-none mt-0.5">
                    Project
                </span>
            </div>
            {/* satellites */}
            {TALENT_ROLES.map((r) => {
                const rad = (r.angle * Math.PI) / 180;
                const x = Math.cos(rad) * radius;
                const y = Math.sin(rad) * radius;
                return (
                    <div
                        key={r.label}
                        className="absolute z-10"
                        style={{
                            left: `calc(50% + ${x}px)`,
                            top: `calc(50% + ${y}px)`,
                            transform: "translate(-50%, -50%)",
                        }}
                    >
                        <div className="flex items-center gap-1.5 rounded-full bg-white ring-1 ring-black/[0.10] pl-1.5 pr-2.5 py-1 shadow-soft">
                            <span className="inline-flex h-5 w-5 items-center justify-center rounded-full bg-[rgba(187,118,33,0.12)] text-[hsl(var(--gold))]">
                                <r.Icon className="h-2.5 w-2.5" />
                            </span>
                            <span className="text-[10.5px] font-mono uppercase tracking-[0.16em] text-[hsl(var(--text))]">
                                {r.label}
                            </span>
                        </div>
                    </div>
                );
            })}
        </div>
    );
};

// ---------- 04 / Growth Engines ----------
const GrowthEngineDiagram = () => {
    const bars = [22, 30, 28, 42, 38, 55, 64, 58, 76, 84, 92, 100];
    return (
        <div className="flex flex-col gap-4">
            <Row label="Growth · last 12 weeks" className="!pb-2">
                <div className="flex items-end gap-1.5 h-20">
                    {bars.map((h, i) => (
                        <span
                            key={i}
                            style={{ height: `${h}%` }}
                            className={`w-2.5 rounded-sm ${
                                i === bars.length - 1
                                    ? "bg-[hsl(var(--gold))]"
                                    : "bg-[rgba(37,99,235,0.55)]"
                            }`}
                        />
                    ))}
                </div>
            </Row>

            <div className="grid grid-cols-3 gap-2">
                <KpiTile label="New leads" value="3.4×" />
                <KpiTile label="Cost per lead" value="−42%" accent="gold" />
                <KpiTile label="Time to win" value="−31%" accent="gold" />
            </div>

            <div className="flex items-center gap-2 rounded-xl border border-black/[0.08] bg-white px-3 py-2">
                <span className="inline-flex h-6 w-6 items-center justify-center rounded-full bg-[rgba(37,99,235,0.12)] text-[hsl(var(--blue))]">
                    <Check className="h-3 w-3" />
                </span>
                <span className="text-[12px] text-[hsl(var(--text-muted))]">
                    Every dollar tracked, end to end.
                </span>
            </div>
        </div>
    );
};

// ---------- shared bits ----------
const Row = ({ label, children, className = "" }) => (
    <div
        className={`flex flex-col gap-2 pb-3 border-b border-black/[0.06] ${className}`}
    >
        <span className="text-[10px] font-mono uppercase tracking-[0.18em] text-[hsl(var(--text-subtle))]">
            {label}
        </span>
        {children}
    </div>
);

const KpiTile = ({ label, value, accent = "ink" }) => (
    <div className="rounded-xl border border-black/[0.08] bg-white px-3 py-2">
        <div className="text-[9.5px] font-mono uppercase tracking-[0.20em] text-[hsl(var(--text-subtle))]">
            {label}
        </div>
        <div
            className={`font-display text-xl mt-0.5 num-tabular ${
                accent === "gold"
                    ? "text-[hsl(var(--gold))]"
                    : "text-[hsl(var(--text))]"
            }`}
        >
            {value}
        </div>
    </div>
);

export default CapabilityDiagram;
